import React, { HTMLAttributes } from "react";
import cn from "classnames";
import Joi from "joi";
import { Input } from "@trussle/external-components";
import { PurposeInput } from "../common";
import { ErrorMessage } from "./ErrorMessage";
import { CTA } from "./CTA";
import { useFormContext } from "../context/FormContext";
import "../styles.scss";
import { AdditionalBorrowing } from "../../../../types/common";

type IRemoFormProps = HTMLAttributes<HTMLFormElement>;

const schema = Joi.object({
  mortgagePurpose: Joi.string().required().messages({
    "string.empty": "mortgage purpose"
  }),
  propertyValue: Joi.string().required().messages({
    "string.empty": "property value"
  }),
  expiry: Joi.string().required().messages({
    "string.empty": "expiry"
  }),
  additionalBorrowing: Joi.string().required().messages({
    "string.empty": "additional borrowing"
  }),
  balance: Joi.string().required().messages({
    "string.empty": "balance"
  })
});

export const RemoForm: React.FC<IRemoFormProps> = ({ className, ...rest }) => {
  const { formData, setFormData, recalculateStep } = useFormContext();
  const [errors, setErrors] = React.useState<Joi.ValidationError | null>(null);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const inputs = new FormData(e.currentTarget);
    const inputEntries = Object.fromEntries(inputs.entries());

    const { error } = schema.validate(inputEntries, { abortEarly: false });

    if (error) {
      setErrors(error);
    } else {
      setErrors(null);

      recalculateStep();
    }
  };

  return (
    <form {...rest} className={cn("heroff2-form", className)} onSubmit={onSubmit}>
      <h2 className="heroff2-form-heading-remo">Join thousands of homeowners with mortgage advice you can trust.</h2>
      <PurposeInput key="remoform-purposeinput" />
      <Input
        type="month"
        label="When does your mortgage deal expire?"
        name="expiry"
        value={formData.dealExpiry}
        onChange={(e) => setFormData({ ...formData, dealExpiry: e.target.value })}
      />
      <Input
        name="additionalBorrowing"
        variant="select"
        label="Do you require additional borrowing?"
        options={[
          { label: "", value: "", hidden: true },
          { label: "Yes", value: "yes" },
          { label: "No", value: "no" }
        ]}
        value={formData.additionalBorrowing}
        onChange={(e) =>
          setFormData({
            ...formData,
            additionalBorrowing: e.target.value as AdditionalBorrowing
          })}
      />
      <Input
        variant="currency"
        label="How much is left on your mortgage?"
        name="balance"
        value={formData.balance}
        onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
      />
      <ErrorMessage errors={errors} />
      <CTA>Get started</CTA>
    </form>
  );
};
